/**
 * Shared onboard / import form UI fragments for console club flows.
 */

import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { INTERNAL_NOTES_MAX_LENGTH } from "../../utils/consoleOnboardPayload";
import { MAX_CAROUSEL_IMAGES, swapNeighbors } from "../../utils/consoleUtils";

/**
 * Reorderable carousel strip (URLs or blob previews). Used by console club/run modals.
 * @param {{ entries: Array<{ id: string, previewSrc: string, subtitle?: string }>, onMoveUp: (i: number) => void, onMoveDown: (i: number) => void, onRemove: (i: number) => void, emptyHint?: string }} props
 */
export function ConsoleCarouselOrderStrip({ entries, onMoveUp, onMoveDown, onRemove, emptyHint }) {
  const list = Array.isArray(entries) ? entries : [];
  if (list.length === 0) {
    return emptyHint ? <p className="text-xs text-[var(--console-muted)]">{emptyHint}</p> : null;
  }
  return (
    <div className="flex flex-wrap items-end gap-3">
      {list.map((e, i) => (
        <div key={e.id} className="flex flex-col gap-1 items-center max-w-[120px]">
          <img
            src={e.previewSrc}
            alt=""
            className="h-20 w-full max-w-[112px] object-cover rounded border border-[var(--console-border)] bg-[var(--console-surface)]"
            onError={(ev) => {
              ev.target.style.display = "none";
            }}
          />
          <span className="text-[11px] text-[var(--console-muted)] text-center leading-tight px-1">
            {e.subtitle ?? `Slide ${i + 1}`}
          </span>
          <div className="flex flex-wrap gap-0.5 justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tb-btn h-7 w-7 p-0"
              disabled={i === 0}
              onClick={() => onMoveUp(i)}
              aria-label={`Move image ${i + 1} up`}
            >
              <ChevronUp className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tb-btn h-7 w-7 p-0"
              disabled={i === list.length - 1}
              onClick={() => onMoveDown(i)}
              aria-label={`Move image ${i + 1} down`}
            >
              <ChevronDown className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tb-btn text-[11px] px-2 h-7"
              onClick={() => onRemove(i)}
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Multi-image preview modal (blob or remote URLs) used by Clubs and Scrape club flows. */
export function ConsoleImagePreviewDialog({ urls, onClose }) {
  const list = Array.isArray(urls) ? urls : [];
  const open = list.length > 0;
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="console-page max-w-lg bg-[var(--console-surface)] border-[var(--console-border)] p-4 z-[100]"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-[var(--console-ink)] text-sm">
            {list.length > 1 ? `Image preview (${list.length} images)` : "Image preview"}
          </DialogTitle>
        </DialogHeader>
        {list.length === 1 ? (
          <img
            src={list[0]}
            alt="Preview"
            className="w-full max-h-[70vh] object-contain rounded border border-[var(--console-border)]"
          />
        ) : list.length > 1 ? (
          <div className="flex flex-col gap-3 max-h-[70vh] overflow-hidden">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" style={{ minHeight: 0 }}>
              {list.map((src, idx) => (
                <img
                  key={idx}
                  src={src}
                  alt={`Preview ${idx + 1}`}
                  className="flex-shrink-0 w-full max-w-[280px] max-h-[50vh] object-contain rounded border border-[var(--console-border)] bg-[var(--console-surface)]"
                />
              ))}
            </div>
          </div>
        ) : null}
        <DialogFooter className="pt-2">
          <Button type="button" variant="outline" className="tb-btn" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Verify meet-up spot text before PATCH (console club forms). */
export function MeetupVerifyForm({ candidate, onConfirm, onCancel, saving, descriptionInputId = "console-meetup-verify-desc" }) {
  const [description, setDescription] = useState(() =>
    (candidate?.location_place_name || "").trim().slice(0, 500)
  );
  useEffect(() => {
    setDescription((candidate?.location_place_name || "").trim().slice(0, 500));
  }, [candidate]);
  const address = (candidate?.location_text || "").trim().slice(0, 200);
  const handleConfirm = () => {
    if (!address) return;
    onConfirm(address, description.trim().slice(0, 500) || undefined);
  };
  return (
    <div className="border border-[var(--console-border)] rounded-lg p-3 bg-[var(--console-s2)] space-y-3">
      <p className="text-xs font-medium text-[var(--console-ink)]">Verify meetup spot before saving</p>
      <div className="text-xs">
        <span className="text-[var(--console-muted)]">Address: </span>
        <span className="text-[var(--console-ink)]">{address || "—"}</span>
      </div>
      <div className="grid gap-1.5">
        <label htmlFor={descriptionInputId} className="text-xs font-medium text-[var(--console-ink)]">
          Description (landmark or place name only; leave blank if not applicable)
        </label>
        <Input
          id={descriptionInputId}
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 500))}
          placeholder="e.g. North entrance, Regatta Park"
          maxLength={500}
          className="text-sm border-[var(--console-border)] bg-[var(--console-surface)]"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          className="bg-[#1A1916] hover:bg-[#3C3830] text-white"
          disabled={saving}
          onClick={handleConfirm}
        >
          {saving ? "Saving…" : "Confirm & save"}
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Optional HTTPS image URL paste for club hero / profile (onboard or edit). */
export function ConsoleImageUrlPasteRow({
  id,
  value,
  onChange,
  disabled = false,
  disabledHint,
  hint = "Fetched and stored in our storage when you submit.",
  label = "Or paste image URL (https)",
  labelClassName = "text-xs text-muted-foreground",
  inputClassName = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  /** When set, shows a Save control next to the field (caller runs import + PATCH). */
  onSave,
  saveSaving = false,
  saveLabel = "Save",
  saveDisabled = false,
}) {
  const showSave = typeof onSave === "function";
  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className={labelClassName}>
        {label}
      </label>
      {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
      {disabled && disabledHint ? (
        <p className="text-[11px] text-muted-foreground">{disabledHint}</p>
      ) : null}
      <div className="flex flex-wrap gap-2 items-end">
        <Input
          id={id}
          type="url"
          inputMode="url"
          placeholder="https://…"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={showSave ? `${inputClassName} flex-1 min-w-[12rem]` : inputClassName}
          autoComplete="off"
        />
        {showSave ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tb-btn shrink-0"
            disabled={disabled || saveSaving || saveDisabled}
            onClick={() => onSave()}
          >
            {saveSaving ? "Saving…" : saveLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Build ordered carousel paste URL list (onboard / edit). Parent enforces maxCount.
 */
export function ConsoleClubCarouselPasteUrlsSection({
  pasteUrls,
  onPasteUrlsChange,
  maxCount,
  disabled,
  inputId = "club-carousel-paste-url",
  label = "Add carousel image from URL",
  labelClassName = "text-sm font-medium",
  hintClassName = "text-xs text-muted-foreground",
  inputClassName = "flex h-9 w-full min-w-[12rem] flex-1 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  listItemClassName = "border border-border rounded-md px-2 py-1.5",
  urlTextClassName = "truncate flex-1 min-w-0 text-muted-foreground",
}) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(pasteUrls) ? pasteUrls : [];
  const atMax = list.length >= maxCount;
  const add = () => {
    const u = draft.trim();
    if (!u.startsWith("http") || disabled || atMax) return;
    if (list.some((x) => String(x).trim() === u)) return;
    onPasteUrlsChange([...list, u]);
    setDraft("");
  };
  return (
    <div className="grid gap-2">
      <span className={labelClassName}>{label}</span>
      <p className={hintClassName}>
        Paste HTTPS links one at a time. Images are imported to our bucket when you save (max {maxCount} in this list for
        this section).
      </p>
      <div className="flex flex-wrap gap-2 items-end">
        <Input
          id={inputId}
          type="url"
          inputMode="url"
          placeholder="https://…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          disabled={disabled || atMax}
          className={inputClassName}
          autoComplete="off"
        />
        <Button type="button" variant="outline" size="sm" disabled={disabled || atMax} onClick={add} className="shrink-0">
          Add URL
        </Button>
      </div>
      {list.length > 0 ? (
        <ul className="text-sm space-y-1.5 max-w-full">
          {list.map((u, i) => (
            <li key={`${i}-${String(u).slice(-24)}`} className={`flex flex-wrap items-center gap-2 ${listItemClassName}`}>
              <span className={urlTextClassName} title={u}>
                {u}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={disabled}
                onClick={() => onPasteUrlsChange(list.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Renders the internal notes textarea for Add/Import/Edit club forms. Console-only; not shown to users. */
export function renderInternalNotesField({ id, value, onChange, rows = 2 }) {
  return (
    <div className="grid gap-2">
      <label htmlFor={id} className="text-sm font-medium text-[var(--console-ink)]">
        Internal notes <span className="text-[var(--console-muted)] font-normal">(optional, console-only)</span>
      </label>
      <Textarea
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Admin-only notes (not shown to users)"
        rows={rows}
        maxLength={INTERNAL_NOTES_MAX_LENGTH}
        className="resize-none border-[var(--console-border)] bg-[var(--console-surface)]"
      />
    </div>
  );
}

/** Renders a single image field (optional current URL + file input + preview) for onboard and edit club modals. */
export function renderSingleImageField(
  label,
  { required = false, currentUrl = null, file, onFileChange, onPreview, inputId }
) {
  return (
    <div className="grid gap-2">
      <label htmlFor={inputId} className="text-sm font-medium text-[var(--console-ink)]">
        {label}
        {required ? <span className="text-destructive" aria-hidden="true"> *</span> : null}
      </label>
      {currentUrl && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs text-[var(--console-muted)]">
            Current: shown on card. Upload a new file to replace.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tb-btn"
            onClick={() => onPreview(currentUrl)}
          >
            Preview
          </Button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <label
          htmlFor={inputId}
          className="cursor-pointer rounded border-0 bg-[#1A1916] px-3 py-1.5 text-white text-sm inline-block"
        >
          Choose File
        </label>
        <span className="text-sm text-[var(--console-ink)]">
          {file
            ? file.name
            : currentUrl
              ? "Current image in use"
              : "No file chosen"}
        </span>
        <input
          key={`${inputId}-${file ? "f" : "e"}`}
          id={inputId}
          type="file"
          accept="image/*"
          onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
          className="sr-only"
          aria-label={label}
        />
      </div>
      {file && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-[var(--console-muted)]">{file.name}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tb-btn"
            onClick={() => openFilePreview(file, onPreview)}
          >
            Preview
          </Button>
          <Button type="button" variant="outline" size="sm" className="tb-btn" onClick={() => onFileChange(null)}>
            Remove file
          </Button>
        </div>
      )}
    </div>
  );
}

export function openFilePreview(file, onPreview) {
  if (!file || typeof onPreview !== "function") return;
  try {
    const blobUrl = URL.createObjectURL(file);
    onPreview(blobUrl);
  } catch {
    // Ignore; caller can still submit
  }
}

export function openCarouselFilesPreview(files, setImagePreviewUrls) {
  if (!Array.isArray(files) || files.length === 0) return;
  try {
    const urls = files.map((f) => URL.createObjectURL(f));
    setImagePreviewUrls(urls);
  } catch {
    // Ignore
  }
}

/**
 * Add-club / Meetup onboard: hero = carousel slot 1; extra files are slots 2+.
 */
export function AddClubCarouselImagesSection({
  heroFile,
  carouselFiles,
  setCarouselFiles,
  setImagePreviewUrls,
  carouselInputId = "onboard-carousel-images",
  onClearHero,
}) {
  const [thumbUrls, setThumbUrls] = useState([]);
  const maxExtra = heroFile != null ? Math.max(0, MAX_CAROUSEL_IMAGES - 1) : MAX_CAROUSEL_IMAGES;

  useEffect(() => {
    const urls = [];
    try {
      if (heroFile) urls.push(URL.createObjectURL(heroFile));
      for (const f of carouselFiles) urls.push(URL.createObjectURL(f));
    } catch {
      /* ignore */
    }
    setThumbUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [heroFile, carouselFiles]);

  const openStripPreview = () => {
    const urls = [];
    try {
      if (heroFile) urls.push(URL.createObjectURL(heroFile));
      for (const f of carouselFiles) urls.push(URL.createObjectURL(f));
    } catch {
      return;
    }
    if (urls.length === 0) return;
    setImagePreviewUrls(urls);
  };

  const totalChosen = (heroFile ? 1 : 0) + carouselFiles.length;

  const removeThumbAtIndex = (idx) => {
    if (heroFile) {
      if (idx === 0) {
        onClearHero?.();
        return;
      }
      const ci = idx - 1;
      setCarouselFiles((prev) => prev.filter((_, i) => i !== ci));
      return;
    }
    setCarouselFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="grid gap-2">
      <label htmlFor={carouselInputId} className="text-sm font-medium text-[var(--console-ink)]">
        Carousel images <span className="text-[var(--console-muted)] font-normal">(optional)</span>
      </label>
      <p className="text-xs text-[var(--console-muted)]">
        The <strong>hero image</strong> above is slot 1 on the club card (same as the app). Add up to{" "}
        <strong>{maxExtra}</strong> more files here for slots 2–{MAX_CAROUSEL_IMAGES} ({MAX_CAROUSEL_IMAGES} images
        total). Use arrows to reorder carousel files; <strong>Remove</strong> drops a slot.
      </p>
      {thumbUrls.length > 0 ? (
        <div className="flex flex-wrap items-end gap-3">
          {thumbUrls.map((src, idx) => {
            const isHeroSlot = Boolean(heroFile) && idx === 0;
            const carouselIndex = heroFile ? idx - 1 : idx;
            const showReorder =
              !isHeroSlot && carouselIndex >= 0 && carouselIndex < carouselFiles.length;
            return (
              <div key={`thumb-row-${idx}-${src.slice(-12)}`} className="flex flex-col gap-1 items-center">
                <img
                  src={src}
                  alt=""
                  className="h-20 w-28 object-cover rounded border border-[var(--console-border)] bg-[var(--console-surface)]"
                />
                <span className="text-[11px] text-[var(--console-muted)] whitespace-nowrap">
                  {idx + 1}
                  {isHeroSlot ? " · Hero" : ""}
                </span>
                {showReorder && (
                  <div className="flex gap-0.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="tb-btn h-7 w-7 p-0"
                      disabled={carouselIndex <= 0}
                      onClick={() =>
                        setCarouselFiles((prev) => swapNeighbors(prev, carouselIndex, -1))
                      }
                      aria-label="Move carousel image earlier"
                    >
                      <ChevronUp className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="tb-btn h-7 w-7 p-0"
                      disabled={carouselIndex >= carouselFiles.length - 1}
                      onClick={() =>
                        setCarouselFiles((prev) => swapNeighbors(prev, carouselIndex, 1))
                      }
                      aria-label="Move carousel image later"
                    >
                      <ChevronDown className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="tb-btn text-[11px] px-2 py-0.5 h-7"
                  onClick={() => removeThumbAtIndex(idx)}
                >
                  Remove
                </Button>
              </div>
            );
          })}
          <Button type="button" variant="outline" size="sm" className="tb-btn self-center mb-5" onClick={openStripPreview}>
            Preview all
          </Button>
        </div>
      ) : (
        <p className="text-xs text-[var(--console-muted)]">Choose a hero image above to see slot 1 in this row.</p>
      )}
      <div className="flex items-center gap-2">
        <label
          htmlFor={carouselInputId}
          className="cursor-pointer rounded border-0 bg-[#1A1916] px-3 py-1.5 text-white text-sm inline-block"
        >
          Choose Files
        </label>
        <input
          id={carouselInputId}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            const next = Array.from(e.target.files || []);
            setCarouselFiles((prev) => [...prev, ...next].slice(0, maxExtra));
            e.target.value = "";
          }}
          className="sr-only"
          aria-label="Additional carousel images"
        />
      </div>
      {carouselFiles.length > 0 && (
        <span className="text-xs text-[var(--console-muted)]">
          {carouselFiles.length} additional file(s). {totalChosen}/{MAX_CAROUSEL_IMAGES} slots filled.
          {carouselFiles.length >= maxExtra && maxExtra > 0 ? " Max additional images reached." : ""}
        </span>
      )}
    </div>
  );
}
